import React, { useState, useEffect, useContext, useRef } from "react";
import { Helmet } from "react-helmet-async";
import { Box, Button, Container, Typography, Grid } from "@mui/material";
import ExploreOutlinedIcon from "@mui/icons-material/ExploreOutlined";
import ExtensionOutlinedIcon from "@mui/icons-material/ExtensionOutlined";

import GradientIcon from "../components/GradientIcon";
import HaveQuestions from "../components/HaveQuestions";


import AssignmentTurnedInOutlinedIcon from '@mui/icons-material/AssignmentTurnedInOutlined';
import HowToRegOutlinedIcon from '@mui/icons-material/HowToRegOutlined';
import ChecklistOutlinedIcon from '@mui/icons-material/ChecklistOutlined';
import CardGiftcardOutlinedIcon from '@mui/icons-material/CardGiftcardOutlined';
import TouchAppOutlinedIcon from '@mui/icons-material/TouchAppOutlined';

import DAMTrainingCourseBG from "../assets/DAMTrainingCourse-bg.png";
import Hooshyar from "../assets/Hooshyar.png"
import halfCircles from "../assets/half-circles.png";
import office from "../assets/office.png"
import zoom from "../assets/zoom.png"
import email from "../assets/email.png"
import miro from "../assets/miro.png"
import browser from "../assets/browser.png"

import AccessTimeIcon from '@mui/icons-material/AccessTime';
import DesktopWindowsOutlinedIcon from '@mui/icons-material/DesktopWindowsOutlined';
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';

import AutorenewOutlinedIcon from '@mui/icons-material/AutorenewOutlined';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import HubOutlinedIcon from '@mui/icons-material/HubOutlined';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';
import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import ManageSearchOutlinedIcon from '@mui/icons-material/ManageSearchOutlined';
import InsertChartOutlinedIcon from '@mui/icons-material/InsertChartOutlined';
import TimelineOutlinedIcon from '@mui/icons-material/TimelineOutlined';
import CompareArrowsOutlinedIcon from '@mui/icons-material/CompareArrowsOutlined';
import SchemaOutlinedIcon from '@mui/icons-material/SchemaOutlined';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';

import EmojiObjectsOutlinedIcon from '@mui/icons-material/EmojiObjectsOutlined';
import CheckOutlinedIcon from '@mui/icons-material/CheckOutlined';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import TravelExploreOutlinedIcon from '@mui/icons-material/TravelExploreOutlined';
import BarChartOutlinedIcon from '@mui/icons-material/BarChartOutlined';
import SupportAgentOutlinedIcon from '@mui/icons-material/SupportAgentOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import Groups2OutlinedIcon from '@mui/icons-material/Groups2Outlined';
import TrendingUpOutlinedIcon from '@mui/icons-material/TrendingUpOutlined';
import WorkOutlineOutlinedIcon from '@mui/icons-material/WorkOutlineOutlined';
import BuildOutlinedIcon from '@mui/icons-material/BuildOutlined';
import WorkspacePremiumOutlinedIcon from '@mui/icons-material/WorkspacePremiumOutlined';

const gradientHoverSx = {
  position: "relative",
  border: "2px solid transparent",
  backgroundClip: "padding-box",
  transition: "all 0.3s ease",
  "&:hover::before": {
    opacity: 1,
  },
  "&::before": {
    content: '""',
    position: "absolute",
    inset: 0,
    borderRadius: "24px",
    padding: "2px",
    background: "linear-gradient(90deg, #FF7FAC, #5834BA)",
    WebkitMask:
      "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
    WebkitMaskComposite: "xor",
    maskComposite: "exclude",
    opacity: 0,
    transition: "opacity 0.3s ease",
  },
};


const DamTrainingCorporatePage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const courseOutline = [
  {
    title: "1. Introduction to DAM",
    items: [
      "Definition & importance",
      "Content types & collections",
      "User personas",
      "Roles (Author, Reviewer, Approver)",
    ],
    icon: EmojiObjectsOutlinedIcon
  },
  {
    title: "2. DAM Fundamentals",
    items: [
      "Asset lifecycle",
      "Version control",
      "Asset journey (Upload → Approve)",
      "Workflow (Author → Publish)",
    ],
    icon: HowToRegOutlinedIcon
  },
  {
    title: "3. Platform & Architecture",
    items: [
      "DAM platform overview",
      "UI/UX & OOTB config",
      "External integrations",
      "One-way / Two-way sync",
    ],
    icon: HubOutlinedIcon
  },
  {
    title: "4. Metadata & Taxonomy",
    items: [
      "Metadata & custom fields",
      "Taxonomy structure",
      "Tags (system + user)",
      "Folder hierarchy",
    ],
    icon: LocalOfferOutlinedIcon
  },
  {
    title: "5. Asset Ingestion & Management",
    items: [
      "Bulk uploads (Drive, Dropbox, etc.)",
      "Metadata indexing",
      "Synonyms setup",
    ],
    icon: CloudUploadOutlinedIcon
  },
  {
    title: "6. Search & Discovery",
    items: [
      "Fast search & relevancy",
      "Filters",
      "Discovery workflows",
    ],
    icon: TravelExploreOutlinedIcon
  },
  {
    title: "7. User Management & Access Control",
    items: [
      "Users, roles & permissions",
      "Teams & stakeholders",
      "Authentication & access control",
      
    ],
    icon: AdminPanelSettingsOutlinedIcon
  },
  {
    title: "8. Workflow & Collaboration",
    items: [
      "Approval workflows",
      "Asset sharing",
      "Business communication",
      
    ],
    icon: AccountTreeOutlinedIcon
  },
  {
    title: "9. Analytics & Reporting",
    items: [
      "Usage analytics",
      "Performance tracking",
    ],
    icon: BarChartOutlinedIcon
  },
  {
    title: "10. Governance & Compliance",
    items: [
      "Governance documentation",
      "Compliance standards",
      "Change management",
    ],
    icon: VerifiedUserOutlinedIcon
  },
  {
    title: "11. Project Management",
    items: [
      "DAM lifecycle phases",
      "Agile (Scrum, Kanban)",
      "Roadmap & planning",
    ],
    icon: TimelineOutlinedIcon
  },
  {
    title: "12. Vendor Selection & Migration",
    items: [
      "Vendor selection",
      "Migration strategy",
      "Asset mapping",
    ],
    icon: CompareArrowsOutlinedIcon
  },
  {
    title: "13. Onboarding & Training",
    items: [
      "Business readiness",
      "Train-the-trainer",
      "Training sessions",
      
    ],
    icon: SchoolOutlinedIcon
  },
  {
    title: "14. Support & Maintenance",
    items: [
      "Backup & recovery",
      "Ongoing support",
      
    ],
    icon: SupportAgentOutlinedIcon
  },
  {
    title: "15. Documentation & Knowledge Management ",
    items: [
      "Usage analytics",
      "Communication plans",
      "Launch process",
      ],
    icon: DescriptionOutlinedIcon
  },
  {
    title: "16. User Adoption & Change Management",
    items: [
      "Adoption strategies",
      "Communication plans",
      "Launch process",
      
    ],
    icon: Groups2OutlinedIcon
  },
  {
    title: "17. Future Trends in DAM",
    items: [
      "AI in DAM",
      "Emerging technologies",
      "Digital transformation",
    ],
    icon: TrendingUpOutlinedIcon
  },
  {
    title: "18. Case Studies & Best Practices",
    items: [
      "Real-world examples",
      "Industry strategies",
      "Lessons learned",
    ],
    icon: WorkOutlineOutlinedIcon
  },
  {
    title: "19. Hands-on Workshop",
    items: [
      "Practical exercises",
      "Mini DAM project",
      "Problem-solving",
      
    ],
    icon: BuildOutlinedIcon
  },
  {
    title: "20. Final Assessment & Certification",
    items: [
      "Course review",
      "Q&A session",
      "Certification exam",
      
    ],
    icon: WorkspacePremiumOutlinedIcon
  },  
];

  const ITEMS_PER_PAGE = 10;

  const [page, setPage] = useState(1);

  const outlineRef = useRef(null);

  const totalPages = Math.ceil(courseOutline.length / ITEMS_PER_PAGE);

  const paginatedData = courseOutline.slice(
    (page - 1) * ITEMS_PER_PAGE,
    page * ITEMS_PER_PAGE
  );

  const OutlineCard = ({ title, items, icon: Icon }) => (
  <Box
    sx={{
      backgroundColor: "#fff",
      borderRadius: "20px",
      boxShadow: "0px 2px 12px rgba(0, 0, 0, 0.08)",
                ...gradientHoverSx,
      textAlign: {
                  xs: "center",
                  md: "left",
                },
      p: { xs: 3, sm: 4, md: 6 },
      display: "flex",
      flexDirection: {
                  xs: "column",                  
                  md: "row",
                },
      gap: 2,
      alignItems: {
                  xs: "center",                  
                  md: "center",
                },
      width: "100%",
      minWidth: 0,
    }}
  >
    {/* Icon */}

    <GradientIcon
                icon={Icon}
                size={40}
                bordered
                boxSize={80}
                borderRadius={24}
                borderWidth={2}
                angle="90"
                sx={{
                  alignSelf: {
                    xs: "center",
                    sm: "center",
                    md: "flex-start",
                  },
                }}
              />

    {/* Content */}
    <Box >
      <Typography sx={{alignItems: { xs: "flex-start",
                                    sm: "center",
                                    md: "center",},
                      fontSize: "1.5rem"
                  }}
                  fontWeight={700} 

                  mb={1}>
        {title}
      </Typography>

      {items.map((item, i) => (
        <Typography
          key={i}
          sx={{
            fontSize: "1rem",
            color: "#666",
            display: "flex",
            alignItems: { xs: "left",
                          sm: "left",
                          md: "center",},
            gap: 1,
            mb: 0.5,
          }}
            >
            <GradientIcon
                icon={CheckOutlinedIcon}
                size={20}                
                borderRadius={24}
                borderWidth={2}
                angle="90"
                sx={{
                  alignSelf: {
                    xs: "center",
                    sm: "flex-start",
                    md: "flex-start",
                  },
                }}
              />
            {item}
            </Typography>
          ))}
        </Box>
      </Box>
    );



const InfoRow = ({ icon: Icon, label, value, highlight, valueSx }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "flex-start",
      gap: 1.25, // reduced from 2
      py: 1.5,
      px: 2,
      borderRadius: "12px",
      backgroundColor: highlight ? "#F5F5F5" : "transparent",
    }}
  >
    {/* Icon */}
    <Box sx={{ mt: "1px" }}>
      <GradientIcon icon={Icon} size={24} />
    </Box>

    {/* Text */}
    <Box sx={{
                display: "flex",
                flexDirection: {
                  xs: "column", // mobile
                  sm: "row",    // tablet & desktop
                },
                alignItems: {
                  xs: "flex-start",
                  sm: "baseline",
                },
                gap: {
                  xs: 0.5,
                  sm: 1.5,
                },                
              }}>
      <Typography
        sx={{
          fontSize: "0.875rem",
          fontWeight: 600,
          lineHeight: { xs: 1.2, sm: 1.8 },
          mb: { xs: 0.4, sm: 0 },
        }}
      >
        {label}:
      </Typography>

      <Typography
        sx={{
          color: "#555",
          lineHeight: { xs: 1.4, sm: 1.8 },
          ...valueSx,
        }}
      >
        {value}
      </Typography>
    </Box>
  </Box>
);
    const TakeawayItem = ({ icon: Icon, text }) => (

         <Box
            sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  px: 1.5,
                  py: 0.5,
                  borderRadius: "10px",
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  "&:hover": {
                        background: "linear-gradient(107.5deg, rgba(114, 79, 211, 0) 25.72%, rgba(252, 134, 175, 0.1) 88.01%)",
                  },
            }}
        >
        
          {/* Icon container */}
          <GradientIcon icon={Icon} size={24} bordered boxSize={40} borderRadius={10}
                  borderWidth={2}
                  angle="90"/>
          

          {/* Text */}
          <Typography sx={{ color: "#333", fontSize: "1rem" }}>
            {text}
          </Typography>
        </Box>
      );

  const SectionCard = ({ title, children }) => (
        <Box
          sx={{
            borderRadius: "12px",
            overflow: "hidden",
            border: "1px solid #E5E5E5",
            backgroundColor: "#fff",
          }}
        >
          {/* Header */}
          <Box
            sx={{
              backgroundColor: "#342E7E",
              color: "#fff",
              px: 2,
              py: 1.5,
              fontWeight: "semiBold",
              fontSize: {sm: "20px", md: "1.5rem"},
            }}
          >
            {title}
          </Box>

          {/* Content */}
          <Box sx={{ p: 2 ,backgroundColor: "#F9F9F9"}}>{children}</Box>
        </Box>
      );

  const assignments = [
  { title: "Assignment 1", desc: "Covers topics in weeks 1 & 2", weight: "25%" },
  { title: "Assignment 2", desc: "Covers topics in weeks 3 & 4", weight: "25%" },
  { title: "Final Assignment", desc: "Covers topics in weeks 1 thru 6", weight: "25%" },
  { title: "Quiz 1", desc: "Covers topics in weeks 1 & 2", weight: "5%" },
  { title: "Quiz 2", desc: "Covers topics in weeks 3 & 4", weight: "5%" },
  { title: "Quiz 3", desc: "Covers topics in weeks 5 & 6", weight: "5%" },
  { title: "Class Attendance", desc: "attendance is required in all sessions", weight: "10%" },
  { title: "Total", desc: "", weight: "100%" },
];

const Assignments = () => (
  <SectionCard title="Assignments and Quizzes">
    {assignments.map((a, i) => (
      <Box
        key={i}
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          p: 1.5,
          borderRadius: "8px",
          backgroundColor: "#fff",
          mb: 1,
        }}
      >
        <Box sx ={{ fontSize:"1rem"}}>
          <Typography fontWeight={600}>{a.title}</Typography>
          <Typography  color="#777">
            {a.desc}
          </Typography>
        </Box>

        <Box
          sx={{
            backgroundColor: "#F9F9F9",
            px: 1.5,
            py: 0.5,
            borderRadius: "6px",
            fontWeight: 700,
            fontSize:"1.25rem"
          }}
        >
          {a.weight}
        </Box>
      </Box>
    ))}
  </SectionCard>
);

const tools = [
  { name: "MS Office 365", img: office, width: 35, height: 40 },
  { name: "Zoom", img: zoom, width: 40, height: 40 },
  { name: "Email", img: email, width: 40, height: 40 },
  { name: "Miro", img: miro, width: 40, height: 40 },
   { name: "Browser", img: browser, width: 88, height: 32 },
];

const Tools = () => (
  <SectionCard title="Required Tools">
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))",
                               sm: "repeat(5, 1fr)",
                               md: "repeat(4, 1fr)"},
        gap: 2,
      }}
    >
      {tools.map((tool, i) => (
        <Box
          key={i}
          sx={{
            textAlign: "center",
            p: 2,
            borderRadius: "10px",
            border: "1px solid #E5E5E5",
            backgroundColor: "#ffffff",
          }}
        >
          <Box
             component="img"
             src={tool.img}                      
              sx={{
                  width: tool.width,
                  height: tool.height,
                  borderRadius: "12px",
                  objectFit: "cover",
                }}
          />          
          <Typography fontSize={13}>{tool.name}</Typography>
        </Box>
      ))}
    </Box>
  </SectionCard>
);

const schedule = [
  { week: "Week 1", topic: "Introduction to DAM", event: "Quiz 1", date: "Week 2" },
  { week: "Week 2", topic: "Taxonomy & Metadata", event: "Assignment 1 Due", date: "Week 3 Start" },
  { week: "Week 3", topic: "System Migration", event: "Quiz 2", date: "Week 4" },
  { week: "Week 4", topic: "Workflow & Governance", event: "Assignment 2 Due", date: "Week 5 Start" },
  { week: "Week 5", topic: "Integration Strategies", event: "Quiz 3", date: "Week 6" },
  { week: "Week 6", topic: "Final Project & Review", event: "Final Assignment Due", date: "One Week After Week 6 " },
];

const Schedule = () => (
  <SectionCard title="Course Schedule">
   <Box        
        sx={{
          display: "flex",          
          p: 1.5,
          borderRadius: "8px",          
          mb: 1,
        }}>

      <Box sx={{ flex: 1, textAlign: "center" }}>
          <Typography sx={{ fontSize: "1.25rem",fontWeight: "Bold"}}>Weekly Topics</Typography>      
      </Box>

      <Box sx={{ flex: 1, textAlign: "center" }}>
          <Typography sx={{ fontSize: "1.25rem",fontWeight: "Bold"}}>Important Dates</Typography>      
      </Box>
    </Box>

    {schedule.map((s, i) => (

      <Box
        key={i}
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center", // vertical alignment
          p: 1.5,
          borderRadius: "8px",
          backgroundColor: "#ffff",
          mb: 1,
          gap: 2, // spacing between icon and text
        }}
      >
       {/* ICON */}
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <GradientIcon icon={CalendarTodayOutlinedIcon} size={24} angle="90" />
        </Box>

         {/* LEFT COLUMN */}
        <Box sx={{ flex: 1 }}>   

          <Typography sx={{ fontSize: "1rem",fontWeight: "Bold"}}>{s.week}</Typography>
          <Typography sx={{ fontSize: "1rem"}} color="#777">
            {s.topic}
          </Typography>
        </Box>

        {/* RIGHT COLUMN */}
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontSize: "1rem", fontWeight: 700}} >{s.event}</Typography>
          <Typography sx={{ fontSize: "1rem"}} color="#777">
            {s.date}
          </Typography>
        </Box>
      </Box>
    ))}
  </SectionCard>
);

const gradingScale = [
  [
    { grade: "A+", range: "98–100%", label: "Expert" },
    { grade: "A", range: "95–97.9%", label: "Very Excellent" },
    { grade: "A-", range: "90–94.9%", label: "Excellent" },
  ],
  [
    { grade: "B+", range: "87–89.9%", label: "Very Good" },
    { grade: "B", range: "84–86.9%", label: "Good" },
    { grade: "B-", range: "80–83.9%", label: "Fairly Good" },
  ],
  [
    { grade: "C+", range: "77–79.9%", label: "Above Standard" },
    { grade: "C", range: "74–76.9%", label: "Standard" },
    { grade: "C-", range: "70–73.9%", label: "Below Standard" },
  ],
  [
    { grade: "D", range: "60–69.9%", label: "Poor" },
    { grade: "F", range: "< 59.9%", label: "Fail" },
  ],
];

const gradeColors = {
  A: "#EAF7F0",
  B: "#EEF4FF",
  C: "#FFFBF0",
  D: "#FFF1E6",
  F: "#FDECEC",
};


const GradingScale = () => (
  <SectionCard title="Grading Scale">
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>

      {gradingScale.map((row, i) => (
        <Box
          key={i}
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: 2,
            backgroundColor: "#fff",
            borderRadius: "10px",
            p: 1.5,
          }}
        >
          {row.map((g, j) => (
            <Box
              key={j}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
              }}
            >
              {/* Circle Grade */}
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: "50%",
                  backgroundColor: gradeColors[g.grade[0]],
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: "Bold",
                  fontSize: "1rem",
                }}
              >
                {g.grade}
              </Box>

              {/* Text */}
              <Box>
                <Typography sx={{ fontWeight: 600, fontSize: "1rem" }}>
                  {g.range}
                </Typography>
                <Typography sx={{ fontSize: "0.8rem", color: "#777" }}>
                  {g.label}
                </Typography>
              </Box>
            </Box>
          ))}

          {/* Fill empty cells for last row */}
          {row.length < 3 &&
            Array.from({ length: 3 - row.length }).map((_, k) => (
              <Box key={`empty-${k}`} />
            ))}
        </Box>
      ))}

    </Box>
  </SectionCard>
);
  return (
    <Box sx={{ backgroundColor: "#F9F9F9" }}>
      <Helmet>
        <title>DAM Journey - DAM Training Academic</title>
        <meta
          name="description"
          content="DAM Training Course, Master the principles of Digital Asset Management through a structured, product-agnostic training program designed for real-world implementation"
        />
        <meta
          property="keywords"
          content="DAM, DAM Journey, DAM Practice, DAM Consultant, DAM Consulting, DAM Practitioner, DAM Service, DAM Product, DAM Discovery, DAM Implementation, DAM Deployment, DAM Training Course, DAM Training Academic"
        />
      </Helmet>
      <Box >
        {/* Hero Banner Section */}
        <Box
          sx={{
            width: "100%",
            height: "350px",
            background: "linear-gradient(0deg, #2A2177 0%, #0624BE 100%)",
            display: "flex",
            alignItems: "center",
            pl: "84px",
            pr: "219px",
            py: "74px",
            overflow: "hidden",
            position: "relative",
            "@media (max-width: 1200px)": {
              px: "24px",
              py: "78px",
            },
            "@media (max-width: 780px)": {
              flexDirection: "column",
              textAlign: "center",
              px: "16px",
              pt: "48px",
              pb: 2,
              height: "420px",
            },
          }}
        >
          {/* Left Text Content */}
          <Box
            sx={{
              flex: 2,
              zIndex: 1,
            }}
          >
            <Typography
              variant="h1"
              sx={{
                fontSize: "2.5rem",
                fontWeight: 700,
                color: "#ffffff",
                lineHeight: 1.3,
                mb: 2,
                "@media (max-width: 1200px)": {
                  fontSize: "1.75rem",
                  fontWeight: "semiBold",
                },
                "@media (max-width: 780px)": {
                  fontSize: "1.5rem",
                  mb: 1,
                },
              }}
            >
              DAM Training Course
              
            </Typography>
            <Typography
              sx={{
                fontSize: "20px",
                fontWeight: "regular",
                color: "#ffffff",
                lineHeight: 1.7,
                maxWidth: "624px",
                "@media (max-width: 1200px)": {
                  fontSize: "18px",
                },
                "@media (max-width: 780px)": {
                  fontSize: "16px",
                },
              }}
            >
              Master the principles of Digital Asset Management through a structured, 
              product-agnostic training program designed for real-world implementation.
            </Typography>
          </Box>

          {/* Right DAM Training Course BG Image */}
          <Box
            sx={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              position: "relative",
            }}
          >
            {/* Background Half Circles */}
            <Box
              component="img"
              src={halfCircles}
              aria-hidden="true"
              sx={{
                position: "absolute",
                width: "599px",
                height: "350px",
                objectFit: "contain",
                left: -40,
                "@media (max-width: 1200px)": {
                  left: 0,
                },
                "@media (max-width: 780px)": {
                  left: "50%",
                  transform: "translateX(-50%)",
                  width: "370px",
                  height: "auto",
                },
                zIndex: 0,
              }}
            />
            {/* Foreground DAM Training Course-bg */}
            <Box
              component="img"
              src={DAMTrainingCourseBG}
              sx={{
                position: "relative",
                zIndex: 1,
                width: "541px",
                height: "231px",
                "@media (max-width: 1200px)": {
                  width: "541px",
                  height: "231px",
                },
                "@media (max-width: 780px)": {
                  fontSize: "18px",
                  width: "317px",
                  height: "135px",
                },
              }}
            />
          </Box>
        </Box>



        {/* Course Description Section */}

        <Box
          sx={{
            py: {xs: "30px",
                   sm: "48px",
                   lg: "48px"},

            px:   {xs: "16px",
                   sm: "24px",
                   lg: "84px"}            
          }}
        >
       
          <Typography
            variant="h2"
            component="h2"
            sx={{
              fontSize: "3rem",
              fontWeight: "bold",
              textAlign: "center",
              color: "#2A2177",
              textAlign: "center",
              mb: 2,
              "@media (max-width: 1200px)": {
                fontSize: "2.5rem",
              },
              "@media (max-width: 780px)": {
                fontSize: "2rem",
              },
            }}
          >
            Course Description
          </Typography>
          <Typography
            sx={{
              fontSize: "18px",
              fontWeight: "regular",
              textAlign: "center",
              color: "#666666",
              lineHeight: 1.7,
              mb: 5,
              "@media (max-width: 780px)": {
                fontSize: "16px",
              },
            }}
          >
            <p>
              A Digital Asset Management (DAM) system provides a centralized platform for storing,
              organizing, managing, retrieving, and distributing digital files across all teams and 
              departments within an organization. It plays a critical role in enabling fast content 
              discoverability with high relevancy.
            </p>
            <p>
              DAM is the cornerstone of digital transformation initiatives and serves as a 
              “single source of truth” for all digital assets. This training program is designed to provide a deep, 
              product-agnostic understanding of DAM principles, enabling participants to apply their knowledge 
              across any DAM platform.
            </p>
            <p>
             This course is neutral about any DAM software product. 
             This means that the course material is not developed around a particular DAM product, 
             so you are not locked into learning one software product. Essentially, 
             you will gain a deep understanding of the principles of DAM so that you apply them 
             to any DAM product that you may be using now or in the future.
            </p>
          </Typography>
        </Box>

          {/* Course Details & Course Takeaways Cards */}
          
          <Box
            sx={{
              display: "flex",               
              alignItems: {xs: "stretch", md: "flex-start" },             
              flexDirection: { xs: "column", md: "row" },
              gap: 4,
              justifyContent: "center",
              backgroundColor: "#ffffff",  
              borderTop: "1px solid rgba(0, 0, 0, 0.1)",                          
              py: {xs: "30px",
                   sm: "48px",
                   lg: "48px"},

              px:   {xs: "16px",
                   sm: "24px",
                   lg: "84px"}                      
            }}
          >
            {/* Course Details Card */}
            <Box
                sx={{
                  flex: 1,
                  backgroundColor: "#ffffff",
                  borderRadius: "24px",
                  p: 4,
                  boxShadow: "0px 2px 12px rgba(0, 0, 0, 0.08)",
                }}
              >
                {/* Header */}
                <Typography
                  sx={{
                   fontSize: "2.5rem",
                   "@media (max-width: 780px)": {
                      fontSize: "2rem",
                      textAlign: "center",
                    },
                    fontWeight: 700,
                    color: "#2A2177",
                    mb: 1,
                  }}
                >
                  Course Details
                </Typography>

                <Typography
                  sx={{
                    fontSize: "1.5rem",
                    "@media (max-width: 780px)": {
                      fontSize: "1.25rem",
                      textAlign: "center",
                    },
                    color: "#333",
                    mb: 3,
                  }}
                >
                  Principles of Digital Asset Management
                </Typography>

                   {/* Info Rows */}
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                    <InfoRow
                      icon={AccessTimeIcon}
                      label="Duration"
                      value="6 Weeks"
                    />

                    <InfoRow
                      icon={DesktopWindowsOutlinedIcon}
                      label="Format"
                      value="Online (Zoom + Interactive Sessions)"
                      highlight
                    />

                    <InfoRow
                      icon={EventOutlinedIcon}
                      label="Session Timing"
                      value="Thursday, 6:00 PM – 8:40 PM PST"
                    />

                    <InfoRow
                      icon={SchoolOutlinedIcon}
                      label="Instructor"
                      value="Hooshyar Naraghi"
                      highlight
                      valueSx={{ fontWeight: 600 }}
                    />
                  </Box>

                  {/* Description */}
                  <Box
                    sx={{
                      display: "flex",
                      gap: 2,
                      mt: 3,
                      alignItems: "flex-start",
                    }}
                  >
                    <Box
                      component="img"
                      src={Hooshyar}
                      
                      sx={{
                        width: 60,
                        height: 60,
                        borderRadius: "12px",
                        objectFit: "cover",
                      }}
                    />

                      <Typography sx={{ color: "#555", mb: 1 ,fontSize: "0.875rem"}}>
                        This course provides a comprehensive understanding of Digital Asset
                        Management principles, focusing on organization-wide asset strategy,
                        discoverability, governance, and workflows.
                      </Typography>                    
                  </Box>

                  <Box sx={{display: "flex",gap: 0,mt: 0,alignItems: "flex-start",fontSize: "0.875rem"}}>
                    <Typography sx={{ color: "#555" }}>
                        <p>
                          Hooshyar Naraghi is the principal consultant at DAM Journey, LLC. 
                          He is a subject matter expert in digital asset management (DAM), 
                          content management systems (CMS), assets taxonomy and classification, 
                          data migrations, and training with more than two decades of experience 
                          across various business and engineering sectors. He operates a DAM practice 
                          serving clients globally. Some of the companies he has consulted with are Apple Computers, 
                          Moderna, Charter Communications, Culligan International, TD Ameritrade (now Charles Schwab), 
                          ServiceNow, Brocade Communications (now Broadcom), Altair Engineering (now Siemens), 
                          PLOS, AbbVie, and LINQ.
                        </p>
                        <p>
                          Hooshyar also has extensive experience delivering Digital Asset Management (DAM) 
                          training at both corporate and academic levels. 
                          The DAM curriculum he developed and teaches is considered one of the most 
                          comprehensive programs in the DAM industry.
                        </p>
                    </Typography>
                  </Box>
                </Box>

            {/* Course Takeaways Card */}

            <Box
              sx={{
                flex: 1,
                backgroundColor: "#ffffff",
                borderRadius: "24px",
                p: 4,
                boxShadow: "0px 2px 12px rgba(0, 0, 0, 0.08)",
              }}
            >
              {/* Header */}
                <Typography
                  sx={{
                    fontSize: "2.5rem",
                    "@media (max-width: 780px)": {
                      fontSize: "2rem",
                    },
                    fontWeight: 700,
                    color: "#2A2177",
                    mb: 1,
                  }}
                >
                  Course Takeaways
                </Typography>

                <Typography
                  
                  sx={{
                    fontSize: "1.5rem",
                    "@media (max-width: 780px)": {
                      fontSize: "1.25rem",
                    },
                    color: "#333",
                    mb: 3,
                    fontWeight: 500,
                  }}
                >
                  Practical skills and insights to apply DAM effectively
                </Typography>

            {/* Takeaways */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0 }}>
              {/* items here */}             
                  <TakeawayItem
                    icon={AutorenewOutlinedIcon}
                    text="Understand DAM fundamentals and asset lifecycle"
                  />
                  <TakeawayItem
                    icon={LocalOfferOutlinedIcon}
                    text="Learn metadata, taxonomy, tagging, and folder structure strategies"
                  />
                  <TakeawayItem
                    icon={AccountTreeOutlinedIcon}
                    text="Master asset workflows, share links, and collaboration"
                  />
                  <TakeawayItem
                    icon={HubOutlinedIcon}
                    text="Explore DAM architecture and integrations"
                  />
                  <TakeawayItem
                    icon={VerifiedUserOutlinedIcon}
                    text="Gain insights into governance and compliance"
                  />
                  <TakeawayItem
                    icon={AdminPanelSettingsOutlinedIcon}
                    text="Learn user management and access control"
                  />
                  <TakeawayItem
                    icon={ManageSearchOutlinedIcon}
                    text="Define asset search and discovery"
                  />
                  <TakeawayItem
                    icon={InsertChartOutlinedIcon}
                    text="Understand analytics and reporting"
                  />
                  <TakeawayItem
                    icon={TimelineOutlinedIcon}
                    text="Plan DAM implementation projects"
                  />
                  <TakeawayItem
                    icon={CompareArrowsOutlinedIcon}
                    text="Explore DAM vendor selection and RFP creation"
                  />
                  <TakeawayItem
                    icon={SchemaOutlinedIcon}
                    text="Master asset migration and asset mapping"
                  />
                  <TakeawayItem
                    icon={LightbulbOutlinedIcon}
                    text="Learn best practices from real-world case studies"
                  />
                
            </Box>                  
          </Box>
          </Box>
    
   
        
        {/* Course Outline Section */}
        <Box

          sx={{
            borderTop: "1px solid rgba(0, 0, 0, 0.1)",
            py: {xs: "30px",
                   sm: "48px",
                   md: "48px"},

            px:   {xs: "16px",
                   sm: "16px",
                   md: "48px"} ,            
          }}
        >
          <Typography
            ref={outlineRef}
            variant="h2"
            component="h2"
            sx={{
              fontSize: { xs: "2rem", sm: "2.5rem", md: "3rem" },
              fontWeight: "bold",
              textAlign: "center",
              color: "#2A2177",
              mb: 2,
              scrollMarginTop: "150px", 
              
            }}
          >
            Course Outline
          </Typography>
          <Typography
            sx={{
              fontSize: "18px",
              "@media (max-width: 780px)": {
                fontSize: "16px",
              },
              fontWeight: "regular",
              textAlign: "center",
              color: "#666666",
              lineHeight: 1.7,
              mb: 5,
            }}
          >
            A comprehensive breakdown of Digital Asset Management concepts, tools, and best practices.
          </Typography>
          {/* Cards Container */}

          <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(2, 1fr)",

                "@media (max-width: 780px)": {
                  gridTemplateColumns: "1fr"
                },
                gap: {
                  xs: 2,
                  sm: 2,
                  md: 3,
                },
                width: "100%",
                maxWidth: "1280px",
                mx: "auto",
                alignItems: "stretch",                             
              }}
            >

            {paginatedData.map((card, index) => (
              <OutlineCard key={index} {...card} />
            ))}         

          </Box>

          <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  mt: 4,
                  gap: 1,
                }}
              >
                {[...Array(totalPages)].map((_, i) => (
                  <Box
                    key={i}
                    onClick={() => {
                      setPage(i + 1);
                      outlineRef.current?.scrollIntoView({
                        behavior: "smooth",
                        block: "start",
                      });
                    }}
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: "8px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                      backgroundColor: page === i + 1 ? "#2A2177" : "#eee",
                      color: page === i + 1 ? "#fff" : "#000",
                      fontSize: "14px",
                    }}
                  >
              {i + 1}
            </Box>
            ))}
          </Box>
        </Box>

        {/* Course Registration Section */}
        <Box
          sx={{
            borderTop: "1px solid rgba(0, 0, 0, 0.1)",
            py: {xs: "30px",
                   sm: "48px",
                   md: "48px"},

            px:   {xs: "16px",
                   sm: "16px",
                   md: "48px"} ,
            backgroundColor: "#ffffff",
                         
          }}
        >
          <Typography
            variant="h2"
            component="h2"
            sx={{
              fontSize: { xs: "2rem", sm: "2.5rem", md: "3rem" },
              fontWeight: "bold",
              textAlign: "center",
              color: "#2A2177",
              mb: 2,
              
            }}
          >
            Course Registration
          </Typography>
          <Typography
            sx={{
              fontSize: "18px",
              "@media (max-width: 780px)": {
                fontSize: "16px",
              },
              fontWeight: "regular",
              textAlign: "center",
              color: "#666666",
              lineHeight: 1.7,
              mb: 5,
            }}
          >
            This DAM Training Course provides a platform for professionals to learn and experience 
            the fundamentals of Digital Asset Management, covering its principles, practices, and 
            techniques through an immersive experience.
          </Typography>
          {/* Cards Container */}
          <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(2, 1fr)",

                "@media (max-width: 780px)": {
                  gridTemplateColumns: "1fr"
                },
                gap: {
                  xs: 2,
                  sm: 2,
                  md: 3,
                },
                alignItems: "stretch",                
                maxWidth: "1280px",
                mx: "auto",
              }}
            >
          
            {/* Who Should Attend */}
            <Box
              sx={{
                width: "100%",
                minWidth: 0,                
                backgroundColor: "#ffffff",
                borderRadius: "24px",
                display: "flex",
                flexDirection: {
                  xs: "column",
                  sm: "column",
                  md: "row",
                },
                boxShadow: "0px 2px 12px rgba(0, 0, 0, 0.08)",
                ...gradientHoverSx,
                alignItems: {
                  xs: "center",
                  sm: "flex-start",
                  md: "center",
                },

                textAlign: {
                  xs: "center",
                  md: "left",
                },
                p: { xs: 3, sm: 4, md: 6 },
                gap: 3,
              }}
            >
              <GradientIcon
                icon={HowToRegOutlinedIcon}
                size={40}
                bordered
                boxSize={80}
                borderRadius={24}
                borderWidth={2}
                angle="90"
                sx={{
                  alignSelf: {
                    xs: "center",
                    sm: "center",
                    md: "flex-start",
                  },
                }}
              />
              <Box>
                <Typography
                  sx={{
                    fontSize: "1.5rem",
                    fontWeight: 700,
                    mb: 0.5,
                    color: "#000000",
                  }}
                >
                  Who Should Attend
                </Typography>
                <Typography
                  sx={{
                    fontSize: "1rem",
                    fontWeight: 400,
                    color: "#666666",
                    lineHeight: 1.4,
                  }}
                >
                  This training program is for professionals who want to learn the fundamentals 
                  of the DAM in an intensive course coverage. Whether you are a stakeholder or a 
                  project team member planning to roll out or operate a DAM system, 
                  you will benefit greatly from this course.
                </Typography>
              </Box>
            </Box>

            {/* Course Prerequisites */}
            <Box
              sx={{
                width: "100%",
                minWidth: 0,
                
                backgroundColor: "#ffffff",
                boxShadow: "0px 2px 12px rgba(0, 0, 0, 0.08)",
                ...gradientHoverSx,
                borderRadius: "24px",
                display: "flex",
                flexDirection: {
                  xs: "column",
                  sm: "column",
                  md: "row",
                },
                alignItems: {
                  xs: "center",
                  sm: "flex-start",
                  md: "center",
                },

                textAlign: {
                  xs: "center",
                  md: "left",
                },
                p: { xs: 3, sm: 4, md: 6 },
                gap: 3,
              }}
            >
              <GradientIcon
                icon={ChecklistOutlinedIcon}
                size={40}
                bordered
                boxSize={80}
                borderRadius={24}
                borderWidth={2}
                angle="90"
                sx={{
                  alignSelf: {
                    xs: "center",
                    sm: "center",
                    md: "flex-start",
                  },
                }}
              />
              <Box>
                <Typography
                  sx={{
                    fontSize: "1.5rem",
                    fontWeight: 700,
                    mb: 0.5,
                    color: "#000000",
                  }}
                >
                  Course Prerequisites
                </Typography>
                <Typography
                  sx={{
                    fontSize: "1rem",
                    fontWeight: 400,
                    color: "#666666",
                    lineHeight: 1.4,
                  }}
                >
                  No technical prerequisite is required to sign up for this course. 
                  The only  prerequisite is a deep and sincere desire to learn about the DAM. 
                  Also, you should be comfortable working in a collaborative environment and be 
                  willing to learn from and share knowledge with peers.
                </Typography>
              </Box>
            </Box>

            {/* Registration Process */}
            <Box
              sx={{
                width: "100%",
                minWidth: 0,                
                backgroundColor: "#ffffff",
                boxShadow: "0px 2px 12px rgba(0, 0, 0, 0.08)",
                ...gradientHoverSx,
                borderRadius: "24px",
                display: "flex",
                flexDirection: {
                  xs: "column",
                  sm: "column",
                  md: "row",
                },
                alignItems: {
                  xs: "center",
                  sm: "flex-start",
                  md: "center",
                },

                textAlign: {
                  xs: "center",
                  md: "left",
                },
                p: { xs: 3, sm: 4, md: 6 },
                gap: 3,
              }}
            >
              <GradientIcon
                icon={AssignmentTurnedInOutlinedIcon}
                size={40}
                bordered
                boxSize={80}
                borderRadius={24}
                borderWidth={2}
                angle="90"
                sx={{
                  alignSelf: {
                    xs: "center",
                    sm: "center",
                    md: "flex-start",
                  },
                }}
              />
              <Box>
                <Typography
                  sx={{
                    fontSize: "1.5rem",
                    fontWeight: 700,
                    mb: 0.5,
                    color: "#000000",
                  }}
                >
                  Registration Process
                </Typography>
                <Typography
                  sx={{
                    fontSize: "1rem",
                    fontWeight: 400,
                    color: "#666666",
                    lineHeight: 1.4,
                    mb: 2,
                  }}
                >
                  Register for the DAM Training Course by submitting your application form. Click the link.
                </Typography>

                <Button
                  variant="contained"
                  startIcon={<TouchAppOutlinedIcon />}
                  href="/dam-training-registration"
                  sx={{
                    background: "#2A2177",
                    borderRadius: "999px",
                    px: 3,
                    py: 1,
                    textTransform: "none",
                    fontWeight: 500,
                    boxShadow: "0px 4px 10px rgba(0,0,0,0.15)",
                    "&:hover": {
                      background: "linear-gradient(90deg, #32267a, #4c3fc2)",
                    },
                  }}
                  >
                  Application Form
                </Button>

              </Box>
            </Box>

            {/* What You Will Get */}
            <Box
              sx={{
                width: "100%",
                minWidth: 0,               
                backgroundColor: "#ffffff",
                boxShadow: "0px 2px 12px rgba(0, 0, 0, 0.08)",
                ...gradientHoverSx,
                borderRadius: "24px",
                display: "flex",
                flexDirection: {
                  xs: "column",
                  sm: "column",
                  md: "row",
                },
                alignItems: {
                  xs: "center",
                  sm: "flex-start",
                  md: "center",
                },

                textAlign: {
                  xs: "center",
                  md: "left",
                },
                p: { xs: 3, sm: 4, md: 6 },
                gap: 3,
              }}
            >
              <GradientIcon
                icon={CardGiftcardOutlinedIcon}
                size={40}
                bordered
                boxSize={80}
                borderRadius={24}
                borderWidth={2}
                angle="90"
                sx={{
                  alignSelf: {
                    xs: "center",
                    sm: "center",
                    md: "flex-start",
                  },
                }}
              />
              <Box>
                <Typography
                  sx={{
                    fontSize: "1.5rem",
                    fontWeight: 700,
                    mb: 0.5,
                    color: "#000000",
                  }}
                >
                  What You Will Get
                </Typography>
                <Typography
                  sx={{
                    fontSize: "1rem",
                    fontWeight: 400,
                    color: "#666666",
                    lineHeight: 1.4,
                  }}
                >
                  You master the core of digital asset management through a project-based 
                  learning and in an immersive experience. You earn your certificate of 
                  completion as proof of your skills in DAM.
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Assignments and Quizzes/Required Tools/Grading Scale/Course Schedule Section */}
        <Box
          sx={{
            py: { xs: "30px", sm: "48px", md: "48px" },
            px: { xs: "16px", sm: "24px", md: "84px" },
            backgroundColor: "#ffffff",
            borderTop: "1px solid rgba(0, 0, 0, 0.1)",
          }}
        >
        <Box
          sx={{
            maxWidth: "1280px",  
            width: "100%",
            mx: "auto",
            display: "flex",
            flexDirection: { xs: "column",sm: "column", md: "row" },
            gap: 3, // space between columns
            alignItems: { xs: "center",
                          sm: "center", 
                          md: "flex-start"}
            ,
            
          }}
        >
        {/* LEFT COLUMN */}
        <Box
          sx={{
            flex: 1,
            width: "100%",
            display: "flex",
            flexDirection: "column",
            gap: 2, // space between cards
          }}
        >
          <Assignments />
          <GradingScale />
        </Box>

        {/* RIGHT COLUMN */}
        <Box
          sx={{
            flex: 1,
            width: "100%",
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          <Tools />
          <Schedule />
        </Box>
      </Box>
    </Box>


         {/* Have Questions Section */}
       <HaveQuestions />
      </Box>
    </Box>
  );
};

export default DamTrainingCorporatePage;
